import { OrderEvent, PaymentStatus } from '__generated__/atlas-types'

import { ISO_DATE_FORMAT, formatDate, getDateDiff } from 'utils/dates'
import { decimalWithCommas, formatNameWithCode } from 'utils/formatters'

import { EMPTY_CELL_CONTENT } from 'shop/products/utils'

import {
  CellClickedEvent,
  ColDef,
  ValueFormatterParams,
} from 'components/AgGrid/types'

import {
  ActionCell,
  DueDateHeader,
  HeaderTooltip,
  LinesheetCell,
  LinesheetHeader,
  PONumberCell,
  PaymentStatusCell,
  RemindersCell,
} from './CellRenderers'
import { RequestStatusCell } from './CellRenderers/RequestStatusCell'
import {
  RemindersCellData,
  RemindersType,
  TableColumns,
} from './joorPayOrders.types'
import {
  AgGridFilterType,
  FilterDef,
  getFilterConfig,
} from 'features/Orders/components'

export const defaultColDef: ColDef = {
  sortable: true,
}

export const frameworkComponents = {
  ActionCellRenderer: ActionCell,
  PONumberCellRenderer: PONumberCell,
  PaymentStatusCellRenderer: PaymentStatusCell,
  RemindersCellRenderer: RemindersCell,
  DueDateHeader: DueDateHeader,
  HeaderTooltip: HeaderTooltip,
  LinesheetCellRenderer: LinesheetCell,
  RequestStatusCellRendered: RequestStatusCell,
  LinesheetHeader: LinesheetHeader,
}

export const dateValueFormatter =
  (dateFormat: string) => (params: ValueFormatterParams) =>
    !!params.value ? formatDate(params.value, dateFormat) : EMPTY_CELL_CONTENT

const removeMoneyFormat = (value: string | number) => {
  if (typeof value === 'number') {
    return value.toString().replace(/(\d+),(?=[\d,]*\.\d{2}\b)/g, '$1')
  }
  return value.replace(/(\d+),(?=[\d,]*\.\d{2}\b)/g, '$1')
}

const numberValueFormatter = (
  params: ValueFormatterParams,
  withDecimals: boolean = true,
) => {
  return withDecimals
    ? decimalWithCommas(params.value as number)
    : (params.value as number)
}

const numberComparator = (valueA: number, valueB: number) => valueA - valueB

export const formatOrderEvent = ({ value }: { value: OrderEvent }) => {
  if (!value) {
    return EMPTY_CELL_CONTENT
  }
  const { id = undefined, name = '', code = '' } = value
  if (!id) {
    return EMPTY_CELL_CONTENT
  }
  const formattedString = formatNameWithCode(name, code)
  if (!formattedString) {
    return EMPTY_CELL_CONTENT
  }
  return formattedString
}

export const getColumnDefs = (
  dateFormat = ISO_DATE_FORMAT,
  isFetching = true,
): ColDef[] => [
  {
    field: TableColumns.Selection,
    headerName: '',
    checkboxSelection: !isFetching,
    headerCheckboxSelection: !isFetching,
    headerCheckboxSelectionFilteredOnly: true,
    width: 44,
    headerClass: 'checkbox',
    sortable: false,
    pinned: 'left',
    suppressMenu: true,
  },
  {
    field: TableColumns.Actions,
    headerName: 'Actions',
    width: 94,
    headerClass: 'text-center',
    cellStyle: {
      textAlign: 'center',
    },
    sortable: false,
    pinned: 'left',
    suppressMenu: true,
    cellRenderer: 'ActionCellRenderer',
    cellRendererParams: {
      disabled: isFetching,
    },
    onCellClicked: (params: CellClickedEvent): void => {
      if (!isFetching) {
        // TODO Review this error. contextMenuFactory is private.
        // @ts-ignore
        params.api.contextMenuFactory.showMenu(
          params.node,
          params.column,
          params.value,
          params.event,
        )
      }
    },
    cellClass: 'action-cell',
  },
  {
    field: TableColumns.PO,
    headerName: 'PO#',
    type: 'rightAligned',
    cellRenderer: 'PONumberCellRenderer',
    headerClass: ['notranslate'],
    cellClass: ['align-right', 'notranslate'],
  },
  {
    field: TableColumns.RetailerName,
    headerName: 'Retailer',
    cellClass: ['notranslate'],
  },
  {
    field: TableColumns.Currency,
    headerName: 'Currency',
    width: 104,
    cellClass: ['notranslate'],
  },
  {
    field: TableColumns.TotalDue,
    headerName: 'Total Due',
    type: 'numericColumn',
    width: 150,
    getQuickFilterText: ({ value }: { value: string }) =>
      removeMoneyFormat(value),
    cellClass: ['align-right', 'notranslate'],
    valueFormatter: numberValueFormatter,
    comparator: numberComparator,
  },
  {
    field: TableColumns.TotalUnits,
    headerName: 'Order Units',
    type: 'numericColumn',
    width: 150,
    cellClass: ['align-right', 'notranslate'],
    valueFormatter: (params) => numberValueFormatter(params, false),
    comparator: numberComparator,
  },
  {
    field: TableColumns.PaidAmount,
    headerName: 'Amount Paid',
    type: 'numericColumn',
    width: 178,
    cellClass: ['align-right', 'notranslate'],
    valueFormatter: numberValueFormatter,
    comparator: numberComparator,
  },
  {
    field: TableColumns.RemainingBalance,
    headerName: 'Remaining Balance',
    type: 'numericColumn',
    width: 178,
    cellClass: ['align-right', 'notranslate'],
    valueFormatter: numberValueFormatter,
    comparator: numberComparator,
  },
  {
    field: TableColumns.DueDate,
    headerName: 'Due Date',
    headerComponent: 'DueDateHeader',
    headerTooltip: 'Due date is based on most recent request.',
    tooltipComponent: 'HeaderTooltip',
    type: 'rightAligned',
    width: 166,
    valueFormatter: dateValueFormatter(dateFormat),
    ...getFilterConfig(AgGridFilterType.Date, { isRange: true }),
    getQuickFilterText: ({ value }: { value: Date }) =>
      formatDate(value, dateFormat),
    cellClass: ['align-right', 'notranslate'],
  },
  {
    field: TableColumns.PaymentStatus,
    headerName: 'Payment Status',
    width: 156,
    cellRenderer: 'PaymentStatusCellRenderer',
  },
  {
    field: TableColumns.PaymentMethod,
    headerName: 'Payment Method',
    width: 200,
  },
  {
    field: TableColumns.Reminders,
    headerName: 'Reminders',
    width: 322,
    cellRenderer: 'RemindersCellRenderer',
    comparator: (reminderA: RemindersCellData, reminderB: RemindersCellData) =>
      reminderA.message.localeCompare(reminderB.message),
  },
  {
    field: TableColumns.ShipStart,
    headerName: 'Start Ship',
    width: 134,
    type: 'rightAligned',
    valueFormatter: ({ value }: { value: Date }) => {
      if (!value) {
        return EMPTY_CELL_CONTENT
      }
      return formatDate(value, dateFormat)
    },
    getQuickFilterText: ({ value }: { value: Date }) =>
      formatDate(value, dateFormat),
    cellClass: ['align-right', 'notranslate'],
  },
  {
    field: TableColumns.ShipComplete,
    headerName: 'Complete Ship',
    width: 134,
    type: 'rightAligned',
    valueFormatter: ({ value }: { value: Date }) => {
      if (!value) {
        return EMPTY_CELL_CONTENT
      }
      return formatDate(value, dateFormat)
    },
    getQuickFilterText: ({ value }: { value: Date }) =>
      formatDate(value, dateFormat),
    cellClass: ['align-right', 'notranslate'],
  },
  {
    field: TableColumns.OrderCreatedDate,
    headerName: 'Order Created',
    width: 134,
    type: 'rightAligned',
    valueFormatter: dateValueFormatter(dateFormat),
    ...getFilterConfig(AgGridFilterType.Date, { isRange: true }),
    getQuickFilterText: ({ value }: { value: Date }) =>
      formatDate(value, dateFormat),
    cellClass: ['align-right', 'notranslate'],
  },
  {
    field: TableColumns.TotalOrderValue,
    headerName: 'Total Order Value',
    type: 'numericColumn',
    width: 178,
    cellClass: ['align-right', 'notranslate'],
    valueFormatter: numberValueFormatter,
    comparator: numberComparator,
  },
  {
    field: TableColumns.SubtotalWholesale,
    headerName: 'Subtotal Wholesale',
    type: 'numericColumn',
    width: 178,
    cellClass: ['align-right', 'notranslate'],
    valueFormatter: numberValueFormatter,
    comparator: numberComparator,
  },
  {
    field: TableColumns.OrderDiscount,
    headerName: 'Order Discount',
    type: 'numericColumn',
    width: 178,
    cellClass: ['align-right', 'notranslate'],
    valueFormatter: numberValueFormatter,
    comparator: numberComparator,
  },
  {
    field: TableColumns.ShippingFees,
    headerName: 'Shipping Fees',
    type: 'numericColumn',
    width: 178,
    cellClass: ['align-right', 'notranslate'],
    valueFormatter: numberValueFormatter,
    comparator: numberComparator,
  },
  {
    field: TableColumns.Taxes,
    headerName: 'Taxes',
    type: 'numericColumn',
    width: 178,
    cellClass: ['align-right', 'notranslate'],
    valueFormatter: numberValueFormatter,
    comparator: numberComparator,
  },
  {
    field: TableColumns.CustomFees,
    headerName: 'Custom Fees',
    type: 'numericColumn',
    width: 178,
    cellClass: ['align-right', 'notranslate'],
    valueFormatter: numberValueFormatter,
    comparator: numberComparator,
  },
  {
    field: TableColumns.FirstRequest,
    headerName: 'First Request',
    width: 134,
    type: 'rightAligned',
    valueFormatter: dateValueFormatter(dateFormat),
    ...getFilterConfig(AgGridFilterType.Date, { isRange: true }),
    getQuickFilterText: ({ value }: { value: Date }) =>
      formatDate(value, dateFormat),
    cellClass: ['align-right', 'notranslate'],
  },
  {
    field: TableColumns.LastNotified,
    headerName: 'Last Notified',
    width: 134,
    type: 'rightAligned',
    valueFormatter: dateValueFormatter(dateFormat),
    ...getFilterConfig(AgGridFilterType.Date, { isRange: true }),
    getQuickFilterText: ({ value }: { value: Date }) =>
      formatDate(value, dateFormat),
    cellClass: ['align-right', 'notranslate'],
  },
  {
    field: TableColumns.LastTransaction,
    headerName: 'Last Transaction',
    width: 154,
    type: 'rightAligned',
    valueFormatter: dateValueFormatter(dateFormat),
    ...getFilterConfig(AgGridFilterType.Date, { isRange: true }),
    getQuickFilterText: ({ value }: { value: Date }) =>
      formatDate(value, dateFormat),
    cellClass: ['align-right', 'notranslate'],
  },
  {
    field: TableColumns.RequestStatus,
    headerName: 'Request Status',
    width: 144,
    cellRenderer: 'RequestStatusCellRendered',
  },
  {
    field: TableColumns.OrderStatus,
    headerName: 'Order Status',
    width: 134,
  },
  {
    field: TableColumns.Linesheet,
    headerName: 'Linesheet',
    headerComponent: 'LinesheetHeader',
    headerTooltip: 'Only the first linesheet from the order will be displayed.',
    tooltipComponent: 'HeaderTooltip',
    cellRenderer: 'LinesheetCellRenderer',
    cellClass: ['notranslate'],
  },
  {
    field: TableColumns.TrackingNumber,
    headerName: 'Tracking Number',
    type: 'numericColumn',
    cellClass: ['align-right', 'notranslate'],
  },
  {
    field: TableColumns.SalesRep,
    headerName: 'Sales Rep',
    cellClass: ['notranslate'],
  },
  {
    field: TableColumns.Event,
    headerName: 'Event',
    width: 120,
    valueFormatter: formatOrderEvent,
    getQuickFilterText: ({ value }: { value: OrderEvent }) =>
      formatNameWithCode(value?.name ?? '', value?.code ?? ''),
    cellClass: ['notranslate'],
  },
]

export const filterDefs: FilterDef[] = [
  {
    id: TableColumns.RetailerName,
    name: TableColumns.RetailerName,
    label: 'Retailer',
    type: AgGridFilterType.Set,
  },
  {
    id: TableColumns.PaymentStatus,
    name: TableColumns.PaymentStatus,
    label: 'Payment Status',
    type: AgGridFilterType.Set,
  },
  {
    id: TableColumns.RequestStatus,
    name: TableColumns.RequestStatus,
    label: 'Request Status',
    type: AgGridFilterType.Set,
  },
  {
    id: TableColumns.FirstRequest,
    name: TableColumns.FirstRequest,
    label: 'First Request',
    type: AgGridFilterType.Date,
  },
  {
    id: TableColumns.LastNotified,
    name: TableColumns.LastNotified,
    label: 'Last Notified',
    type: AgGridFilterType.Date,
  },
  {
    id: TableColumns.LastTransaction,
    name: TableColumns.LastTransaction,
    label: 'Last Transaction',
    type: AgGridFilterType.Date,
  },
  {
    id: TableColumns.DueDate,
    name: TableColumns.DueDate,
    label: 'Payment Due Date',
    type: AgGridFilterType.Date,
  },
  {
    id: TableColumns.OrderCreatedDate,
    name: TableColumns.OrderCreatedDate,
    label: 'Order Created Date',
    type: AgGridFilterType.Date,
  },
]

interface FormatReminderProps {
  dueDate?: string
  lastNotifiedDate?: string
  paymentStatus?: PaymentStatus | string
}

export const formatReminderCell = ({
  dueDate,
  lastNotifiedDate,
  paymentStatus,
}: FormatReminderProps) => {
  const defaultResponse = { message: EMPTY_CELL_CONTENT, type: undefined }

  if (
    paymentStatus === PaymentStatus.FULLY_PAID ||
    paymentStatus === PaymentStatus.PARTIALLY_PAID
  ) {
    return defaultResponse
  }

  if (!dueDate) {
    if (lastNotifiedDate) {
      const lastNotifiedDifferenceInDays = Math.floor(
        getDateDiff(new Date(), lastNotifiedDate, 'days', true),
      )
      if (lastNotifiedDifferenceInDays > 2) {
        return {
          message: `Request sent ${lastNotifiedDifferenceInDays} days ago. Follow up?`,
          type: RemindersType.WARNING,
        }
      }
    }
  } else {
    const dueDateDifferenceInDays = Math.floor(
      getDateDiff(dueDate, new Date(), 'days', true),
    )

    if (dueDateDifferenceInDays >= 0 && dueDateDifferenceInDays < 6) {
      return {
        message: 'Payment due date approaching.',
        type: RemindersType.WARNING,
      }
    }
    if (dueDateDifferenceInDays < 0) {
      return {
        message: 'Payment past due. Follow up?',
        type: RemindersType.ALERT,
      }
    }
  }

  return defaultResponse
}
